/**
 * One-line Project Intent summary and the short intent labels it uses.
 *
 * Moved verbatim out of `components/siterag/ProjectIntentForm.tsx` so report
 * generation and other server code can summarise intent without importing
 * the form (which re-exports `summarizeProjectIntent` and imports these
 * labels back for its selects).
 */
import type { ProjectIntent } from '../types/siteRag';
import { POWER_BUCKET_LABEL, TIMELINE_BUCKET_LABEL, effectivePhases, effectivePower, effectiveTimeline } from './projectIntent';
import { REACTOR_DESIGN_CATALOG } from '../config/reactorDesignCatalog';
import { getReactorVendor } from '../config/reactorVendorCatalog';
import { isIntentFieldExplicitlyUnknown } from './intent/explicitUnknown';

export const TIMELINE_LABELS: Record<NonNullable<ProjectIntent['timeline']>, string> = {
  'under-2yr': '< 2 yr',
  '2-5yr':     '2–5 yr',
  'over-5yr':  '5+ yr',
  'undecided': 'Undecided',
};

export const COOLING_STRATEGY_LABELS: Record<NonNullable<ProjectIntent['coolingStrategy']>, string> = {
  'air':       'Air',
  'liquid':    'Liquid',
  'hybrid':    'Hybrid',
  'undecided': 'Undecided',
};

export const NUCLEAR_INTEREST_LABELS: Record<NonNullable<ProjectIntent['nuclearInterest']>, string> = {
  'primary':     'Primary plan',
  'considering': 'Considering',
  'no':          'Not interested',
  'undecided':   'Undecided',
};

/**
 * Compact one-line summary helper — feeds the SiteConfigurationPanel's
 * collapsed-state preview. Returns `null` when the intent is the empty
 * default so the panel can show its "Set up" CTA instead.
 *
 * PP4: when `intent.phases` is populated, lead with the phased rollout
 * descriptor ("300 MW cumulative IT (4 phases) · Grid → Gas CC → SMR")
 * instead of the single-phase bucket label.
 */
export function summarizeProjectIntent(intent: ProjectIntent | undefined): string | null {
  if (!intent) return null;
  const bits: string[] = [];
  if (intent.workloadProfile && intent.workloadProfile !== 'mixed') bits.push(intent.workloadProfile);

  const view = effectivePhases(intent);
  if (view.isPhased) {
    // C2 P2-11 — the stamp names the quantity's role: the ultimate figure is
    // the CUMULATIVE final-phase milestone, and a declared IT-load basis says
    // so ("<N> MW cumulative IT (<n> phases)"). Reports quote this stamp in
    // projectIntentOverview, so a bare "Phased <N> MW" invited restating the
    // IT campus as facility demand or generation. A recorded-unknown or unset
    // basis prints no basis word (the prompt treats it as unconfirmed).
    const basis = intent.powerBasis === 'it-load' && !isIntentFieldExplicitlyUnknown(intent, 'powerBasis') ? ' IT' : '';
    const finalPhaseLabel = view.phases[view.phases.length - 1]?.label || `Phase ${view.phaseCount}`;
    const capacity = view.totalCapacityMW != null
      ? `${view.totalCapacityMW.toLocaleString('en-US')} MW cumulative${basis}`
      : view.ultimateCapacityBucket != null
        ? `${POWER_BUCKET_LABEL[view.ultimateCapacityBucket]} cumulative${basis}`
        : view.knownCapacityFloorMW != null
          ? `Ultimate capacity TBD (${view.knownCapacityFloorMW.toLocaleString('en-US')} MW known floor${basis})`
          : 'Phased, capacity TBD';
    bits.push(`${capacity} (${view.phaseCount} phases)`);
    bits.push(view.techMixSummary);
    if (view.initialInServiceDate && view.ultimateInServiceDate) {
      bits.push(`${view.initialInServiceDate} → ${view.ultimateInServiceDate}`);
    } else if (view.initialInServiceDate && view.ultimateCodTimeline) {
      bits.push(
        `Phase 1 COD ${view.initialInServiceDate}; ${finalPhaseLabel} ${TIMELINE_BUCKET_LABEL[view.ultimateCodTimeline]} (planning target, not a COD)`,
      );
    } else if (view.initialInServiceDate && view.phaseCount > 1) {
      bits.push(
        `Phase 1 COD ${view.initialInServiceDate}; ${finalPhaseLabel} COD TBD`,
      );
    }
    return bits.length === 0 ? null : bits.join(' · ');
  }

  // Project_Intent_Exact_MW PM3 — surface the exact MW in the summary
  // when the user entered one; otherwise fall back to the bucket label.
  const effective = effectivePower(intent);
  if (effective.isExact || (intent.powerRequirement && intent.powerRequirement !== 'undecided')) {
    bits.push(effective.displayLabel);
  }
  if (intent.coolingStrategy && intent.coolingStrategy !== 'undecided') {
    bits.push(`${COOLING_STRATEGY_LABELS[intent.coolingStrategy]} cooling`);
  }
  if (intent.nuclearInterest && intent.nuclearInterest !== 'undecided') {
    bits.push(`Nuclear: ${NUCLEAR_INTEREST_LABELS[intent.nuclearInterest]}`);
  }
  if (
    intent.reactorDesignClass
    && intent.reactorDesignClass !== 'undecided'
    && intent.nuclearInterest !== 'no'
  ) {
    bits.push(`Reactor: ${REACTOR_DESIGN_CATALOG[intent.reactorDesignClass].label}`);
  }
  if (intent.reactorVendorId && intent.nuclearInterest !== 'no') {
    const vendor = getReactorVendor(intent.reactorVendorId);
    if (vendor) bits.push(vendor.label);
  }
  // PMT4 — surface the exact COD in the summary when set; otherwise
  // fall back to the local short-form bucket label (`"2–5 yr"`) to
  // match the legacy summary chrome. Long-form label
  // (`effectiveTimeline().displayLabel === "2-5 years"`) is reserved
  // for the LLM prompt block in `siteEvidence.ts` where readability
  // beats density.
  const effTimeline = effectiveTimeline(intent);
  if (effTimeline.isExact) {
    bits.push(effTimeline.displayLabel);
  } else if (intent.timeline && intent.timeline !== 'undecided') {
    bits.push(TIMELINE_LABELS[intent.timeline]);
  }
  return bits.length === 0 ? null : bits.join(' · ');
}
